import { apiClient } from "@/lib/api";
import type {
  StandardResponse,
  PaginatedStandardResponse,
  PaginationInfo,
  MessageResponse,
} from "@/types/api-response";
import { extractData, extractPaginatedData } from "@/types/api-response";
import type {
  PlatformSettingListDto,
  PlatformSettingDetailDto,
  CreatePlatformSettingRequest,
  UpdatePlatformSettingRequest,
  PlatformSettingFilters,
  VmSettingsDto,
  UpdateVmSettingsRequest,
} from "@/types/platform-setting";

/**
 * Indexes a tab's rows by setting type. The server sends only the global rows of that tab's types,
 * in no particular order. Two rows of one type means an operator added a second under another key;
 * the oldest wins, so the form shows the same row on every load instead of whichever arrived last.
 */
function indexByType(
  rows: PlatformSettingListDto[],
): Record<string, PlatformSettingListDto> {
  const byType: Record<string, PlatformSettingListDto> = {};
  for (const row of rows) {
    const kept = byType[row.type];
    if (!kept || Date.parse(row.createdAt) < Date.parse(kept.createdAt)) {
      byType[row.type] = row;
    }
  }
  return byType;
}

export const adminPlatformSettingService = {
  async getSettings(
    filters: PlatformSettingFilters,
  ): Promise<{ data: PlatformSettingListDto[]; pagination: PaginationInfo }> {
    const params = new URLSearchParams();
    if (filters.type) params.append("type", filters.type);
    if (filters.key) params.append("key", filters.key);
    if (filters.tenantId) params.append("tenantId", filters.tenantId);
    if (filters.region) params.append("region", filters.region);
    params.append("page", (filters.page ?? 1).toString());
    params.append("limit", (filters.limit ?? 20).toString());

    const response = await apiClient.get<
      PaginatedStandardResponse<PlatformSettingListDto>
    >(`/api/v1/admin/platform-settings?${params.toString()}`);
    return extractPaginatedData(response.data);
  },

  async getSettingById(id: string): Promise<PlatformSettingDetailDto> {
    const response = await apiClient.get<
      StandardResponse<PlatformSettingDetailDto>
    >(`/api/v1/admin/platform-settings/${id}`);
    return extractData(response.data);
  },

  async getFeatureFlags(): Promise<PlatformSettingListDto[]> {
    const response = await apiClient.get<
      StandardResponse<PlatformSettingListDto[]>
    >("/api/v1/admin/platform-settings/feature-flags");
    return extractData(response.data);
  },

  async createSetting(
    data: CreatePlatformSettingRequest,
  ): Promise<PlatformSettingDetailDto> {
    const response = await apiClient.post<
      StandardResponse<PlatformSettingDetailDto>
    >("/api/v1/admin/platform-settings", data);
    return extractData(response.data);
  },

  async updateSetting(
    id: string,
    data: UpdatePlatformSettingRequest,
  ): Promise<PlatformSettingDetailDto> {
    const response = await apiClient.put<
      StandardResponse<PlatformSettingDetailDto>
    >(`/api/v1/admin/platform-settings/${id}`, data);
    return extractData(response.data);
  },

  async getSmtpSettings(): Promise<Record<string, PlatformSettingListDto>> {
    const response = await apiClient.get<
      StandardResponse<PlatformSettingListDto[]>
    >("/api/v1/admin/platform-settings/smtp");
    return indexByType(extractData(response.data));
  },

  async getStripeSettings(): Promise<Record<string, PlatformSettingListDto>> {
    const response = await apiClient.get<
      StandardResponse<PlatformSettingListDto[]>
    >("/api/v1/admin/platform-settings/stripe");
    return indexByType(extractData(response.data));
  },

  async getVmSettings(): Promise<VmSettingsDto> {
    const response = await apiClient.get<StandardResponse<VmSettingsDto>>(
      "/api/v1/admin/platform-settings/vm",
    );
    return extractData(response.data);
  },

  async updateVmSettings(
    data: UpdateVmSettingsRequest,
  ): Promise<VmSettingsDto> {
    const response = await apiClient.put<StandardResponse<VmSettingsDto>>(
      "/api/v1/admin/platform-settings/vm",
      data,
    );
    return extractData(response.data);
  },

  async sendTestEmail(email: string): Promise<string> {
    const response = await apiClient.post<MessageResponse>(
      "/api/v1/admin/platform-settings/test-email",
      { email },
    );
    return response.data.message ?? "Test email queued";
  },
};
